import type {
  Sheet,
  SheetCellStyle,
  SheetColumn,
  SheetDetail,
  SheetFilter,
  SheetRow,
  SheetRowData,
  SheetViewConfig,
  WorkObjectMove,
} from '@yana/shared';
import { api } from './client';
import { trackSheetOperation } from './sheet-operations';

/**
 * Листы — остальные вкладки рабочей книги (Р-22).
 *
 * Отдельным файлом, а не в сторе: тем же вызовам нужен доступ из настроек
 * (конструктор колонок) и из редактора ячейки (варианты для ссылки).
 */

export interface SheetRowsResponse {
  rows: SheetRow[];
  total: number;
  catalogTotal?: number;
  /** Сегодня по серверу: по нему считается заливка по датам */
  today: string;
}

export interface RefOption {
  id: string;
  label: string;
  hint?: string;
}

// Каталог управляется из настроек даже при снятом ярлыке листа.
// Сервер сохраняет те же права и ограничения отдельных колонок.
const rowBase = (code: string) => code === 'work_objects' ? '/work-objects' : `/sheets/${encodeURIComponent(code)}`;

export const sheetsApi = {
  list: () => api.get<{ sheets: Sheet[] }>('/sheets').then((r) => r.sheets),

  detail: (code: string) => api.get<SheetDetail>(rowBase(code)),

  rows: (
    code: string,
    query: {
      search?: string;
      filters?: SheetFilter[];
      sort?: { field: string; dir: 'asc' | 'desc' }[];
      /** В настройках объектов список плоский, без секций рабочего листа. */
      sortScope?: 'catalog';
      includeDeleted?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.search) params.set('search', query.search);
    // Фильтры и сортировка уезжают строкой JSON: их собирает вид, и в них
    // бывают массивы — в плоские параметры такое не укладывается
    if (query.filters?.length) params.set('filters', JSON.stringify(query.filters));
    if (query.sort?.length) params.set('sort', JSON.stringify(query.sort));
    if (code === 'work_objects' && query.sortScope) params.set('sortScope', query.sortScope);
    if (query.includeDeleted) params.set('includeDeleted', 'true');
    if (query.limit) params.set('limit', String(query.limit));
    if (query.offset) params.set('offset', String(query.offset));
    const qs = params.toString();
    return api.get<SheetRowsResponse>(
      `${rowBase(code)}/rows${qs ? `?${qs}` : ''}`,
    );
  },

  patchRow: (
    code: string,
    id: string,
    patch: {
      data?: SheetRowData;
      fills?: Record<string, string | null>;
      styles?: Record<string, SheetCellStyle | null>;
      sectionId?: string | null;
      sortOrder?: number;
      position?: number;
      move?: WorkObjectMove;
    },
  ) => api.patch<{ ok: true }>(`${rowBase(code)}/rows/${id}`, patch),

  createWorkObject: (data: SheetRowData, position: number) =>
    api.post<{ id: string }>('/work-objects/rows', { data, position }),

  /**
   * Правка многих ячеек одним запросом.
   *
   * Возвращает затронутые строки уже пересчитанными — вычисляемые колонки
   * считает сервер. До этого ради них лист перечитывался целиком после
   * каждой правки: полторы тысячи строк ради двух.
   */
  patchCells: async (
    code: string,
    cells: Array<{
      rowId: string;
      sectionId?: string | null;
      sortOrder?: number;
      data?: SheetRowData;
      fills?: Record<string, string | null>;
      styles?: Record<string, SheetCellStyle | null>;
    }>,
    options: { isApplied?: () => boolean } = {},
  ) => {
    const clientOpId = crypto.randomUUID();
    // Данные могут менять подписи, фильтр и вычисления: их эхо не пропускаем.
    const localComplete = options.isApplied && cells.length > 0 && cells.every((cell) =>
      cell.data === undefined && cell.sectionId === undefined && cell.sortOrder === undefined,
    );
    const settle = localComplete ? trackSheetOperation(clientOpId, code, options.isApplied!) : undefined;
    try {
      const result = await api.patch<{ rows: SheetRow[]; today: string }>(
        `/sheets/${encodeURIComponent(code)}/cells`, { cells, clientOpId },
      );
      settle?.(true);
      return result;
    } catch (error) {
      // Запись могла состояться, а HTTP-ответ потеряться: WS вернёт актуальное состояние.
      settle?.(false);
      throw error;
    }
  },

  /** Объединить прямоугольник. Область в одну ячейку означает «разъединить». */
  merge: (
    code: string,
    input: {
      anchorRowId: string;
      anchorKey: string;
      rowSpan: number;
      colSpan: number;
    },
  ) => api.post<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/merges`, input),

  unmerge: (code: string, anchors: Array<{ rowId: string; key: string }>) =>
    api.post<{ removed: number }>(`/sheets/${encodeURIComponent(code)}/merges/remove`, { anchors }),

  /** След выноса данных: состав скопированного, без значений */
  logCopy: (
    code: string,
    body: { columns: string[]; rows: number; cells: number; rowIds: string[] },
  ) => api.post<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/copy-log`, body),

  createRow: (
    code: string,
    data: SheetRowData,
    sectionId?: string | null,
    /** Куда встать: порядок между соседями. Без него — в конец листа */
    sortOrder?: number,
  ) =>
    api.post<{ id: string }>(`${rowBase(code)}/rows`, {
      data,
      sectionId,
      sortOrder,
    }),

  deleteRow: (code: string, id: string) =>
    api.delete<{ ok: true }>(`${rowBase(code)}/rows/${id}`),

  restoreRow: (code: string, id: string) =>
    api.post<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/rows/${id}/restore`),

  bulkPatch: (code: string, ids: string[], data: SheetRowData) =>
    api.post<{ touched: number }>(`/sheets/${encodeURIComponent(code)}/rows/bulk`, { ids, data }),

  refOptions: (
    code: string,
    columnKey: string,
    query: { q?: string; filterValue?: string; includeId?: string; exact?: boolean; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.filterValue) params.set('filterValue', query.filterValue);
    if (query.includeId) params.set('includeId', query.includeId);
    if (query.exact) params.set('exact', 'true');
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const qs = params.toString();
    return api
      .get<{ options: RefOption[] }>(
        `/sheets/${encodeURIComponent(code)}/ref/${encodeURIComponent(columnKey)}${qs ? `?${qs}` : ''}`,
      )
      .then((r) => r.options);
  },

  // ── Конструктор ──
  create: (input: {
    code: string;
    name: string;
    description?: string | null;
    isShared: boolean;
    visibleToRoles: string[];
    editableByRoles: string[];
  }) => api.post<{ code: string }>('/sheets', input),

  update: (code: string, patch: Record<string, unknown>) =>
    api.patch<{ ok: true }>(`/sheets/${encodeURIComponent(code)}`, patch),

  archive: (code: string) => api.delete<{ ok: true }>(`/sheets/${encodeURIComponent(code)}`),

  /** Корзина листов: убранное не стирается и должно возвращаться */
  trash: () =>
    api
      .get<{
        sheets: Array<{
          code: string;
          name: string;
          archivedAt: string;
          ownerName: string | null;
        }>;
      }>('/sheets/trash')
      .then((r) => r.sheets),

  restore: (code: string) => api.post<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/restore`),

  /** Порядок ярлыков внизу: коды целиком в новом порядке */
  reorder: (codes: string[]) => api.post<{ ok: true }>('/sheets/reorder', { codes }),

  duplicate: (code: string) =>
    api.post<{ code: string }>(`/sheets/${encodeURIComponent(code)}/duplicate`),

  similar: (keys: string[]) =>
    api
      .post<{
        similar: Array<{
          code: string;
          name: string;
          shared: number;
          total: number;
        }>;
      }>('/sheets/similar', { keys })
      .then((r) => r.similar),

  addColumn: (code: string, input: Record<string, unknown>) =>
    api.post<{ id: string }>(`/sheets/${encodeURIComponent(code)}/columns`, input),

  updateColumn: (code: string, id: string, patch: Partial<SheetColumn> | Record<string, unknown>) =>
    api.patch<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/columns/${id}`, patch),

  archiveColumn: (code: string, id: string) =>
    api.delete<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/columns/${id}`),

  restoreColumn: (code: string, id: string) =>
    api.post<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/columns/${id}/restore`),

  archivedColumns: (code: string) =>
    api
      .get<{ columns: Array<{ id: string; key: string; label: string }> }>(
        `/sheets/${encodeURIComponent(code)}/columns/archived`,
      )
      .then((r) => r.columns),

  reorderColumns: (code: string, ids: string[]) =>
    api.post<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/columns/reorder`, { ids }),

  // ── Секции ──
  createSection: (
    code: string,
    input: {
      id?: string;
      title?: string | null;
      body?: string | null;
      color?: string | null;
      sortOrder?: number;
    },
  ) => api.post<{ id: string }>(`/sheets/${encodeURIComponent(code)}/sections`, input),

  updateSection: (
    code: string,
    id: string,
    patch: {
      title?: string | null;
      body?: string | null;
      color?: string | null;
      sortOrder?: number;
    },
  ) => api.patch<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/sections/${id}`, patch),

  deleteSection: (code: string, id: string) =>
    api.delete<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/sections/${id}`),

  saveView: (
    code: string,
    input: {
      name: string;
      config: SheetViewConfig;
      isShared: boolean;
      isDefault: boolean;
      sortOrder?: number;
    },
  ) => api.post<{ id: string }>(`/sheets/${encodeURIComponent(code)}/views`, input),

  deleteView: (code: string, id: string) =>
    api.delete<{ ok: true }>(`/sheets/${encodeURIComponent(code)}/views/${id}`),
};
